<!--*
 * Main view of the application, with information for the user
 *
 * @author   pablo.barco
 * @date     2020-06-29
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <pull-to-refresh>
    <div class="container--fluid" id="content">
      <SubMenuHeader
        :title="$t('welcomeScreen.pageTitle')"
        :show-back-button="false"
        @showVideo="$emit('showVideo')"
      />
      <div class="row">
        <div class="col--12">
          <smtt-contextual-box
            ref="userErrorBox"
            is-visible="false"
            :message-title="errorMessage"
            variation="error"
            dismissable="true"
          />
        </div>
      </div>
      <div v-if="hasNeededItems" class="row">
        <div class="col">
          <b class="title">{{ $t("welcomeScreen.beforeStart.label") }} </b>
          <span class="h5">
            <router-link class="btn--link d-inline d--md-none" to="ItemsNeeded">
              {{ $t("welcomeScreen.itemsYouNeed.link.label") }}
            </router-link>
            <span
              class="btn--link d-none d--md-inline"
              href="#"
              @click="showItemsNeededModal = true"
            >
              {{ $t("welcomeScreen.itemsYouNeed.link.label") }}
            </span>
          </span>
        </div>
      </div>
      <div class="row mt-7 mb-7">
        <div class="col">
          <SurveyProgress
            :progress-bar="progressBar"
            :has-workflow="hasWorkflow"
          />
        </div>
      </div>
      <br />
      <div class="row">
        <div class="col--6">
          <div class="h2">{{ $t("welcomeScreen.yourRoutes.label") }}</div>
        </div>
        <div v-if="canDrillDown" class="col--6 text-right d-none d--md-block">
          <app-button
            variation="primary"
            layout="icon-label"
            icon="search"
            icon-position="right"
            @click="showDrillDownModal = true"
          >
            {{ $t("welcomeScreen.drillDown.button") }}
          </app-button>
        </div>
      </div>
      <div class="row">
        <div class="col--12 mb-5">
          <b class="h4">{{ $t("welcomeScreen.pickRoute.label") }}</b>
        </div>
      </div>

      <div v-if="routesInProgress.length !== 0" class="row">
        <div
          v-for="route of routesInProgress"
          :key="route.routeKey"
          class="col--lg--4 col--md--6 col--12"
        >
          <RouteComponent :route="route" />
        </div>
      </div>

      <div v-if="routesPending.length !== 0" class="row">
        <div
          v-for="route of routesPending"
          :key="route.routeKey"
          class="col--lg--4 col--md--6 col--12"
        >
          <RouteComponent :route="route" />
        </div>
      </div>

      <div v-if="routesCompleted.length !== 0" class="row completed">
        <div class="col">
          <b class="h6">{{ $t("welcomeScreen.routes.completed.label") }}</b>
          <div class="row">
            <div
              v-for="route of routesCompleted"
              :key="route.routeKey"
              class="col--lg--4 col--md--6 col--12"
            >
              <RouteComponent :route="route" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="canDrillDown" class="row mt-5 mb-5 d--md-none">
        <div class="col">
          <app-button
            variation="primary"
            class="mt-5"
            :full-width="true"
            layout="icon-label"
            icon="search"
            icon-position="right"
            @click="showDrillDownMenu"
          >
            {{ $t("welcomeScreen.drillDown.button") }}
          </app-button>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <smtt-toast
            style="border-width: 0px"
            ref="statusErrorToast"
            id="errorToast"
          />
        </div>
      </div>
      <DrillDownModal
        v-if="canDrillDown"
        :show="showDrillDownModal"
        @dismiss="closeDrillDownModal"
      />
      <ItemsNeededModal
        v-if="hasNeededItems"
        :show="showItemsNeededModal"
        @dismiss="closeItemsNeededModal"
      />
    </div>
  </pull-to-refresh>
</template>

<script>
import { mapActions } from "vuex";
import getEnv from "@/utils/env";

import SurveyProgress from "./WelcomeScreenComponents/SurveyProgress";
import RouteComponent from "./WelcomeScreenComponents/RouteComponent";
import SubMenuHeader from "./SubMenuHeader";
import DrillDownModal from "@/components/drillDown/DrillDownModal.vue";
import ItemsNeededModal from "@/components/WelcomeScreenComponents/ItemsNeededModal.vue";
import PullToRefresh from "./PullToRefresh.vue";
import AppButton from "@/components/AppButton";
import Intercom from "@intercom/messenger-js-sdk";

export default {
  name: "WelcomeScreen",
  components: {
    ItemsNeededModal,
    DrillDownModal,
    SubMenuHeader,
    RouteComponent,
    SurveyProgress,
    PullToRefresh,
    AppButton,
  },
  props: {
    message: {
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      errorMessage: null,
      sites: [],
      progressBar: {},
      routesCompleted: [],
      routesInProgress: [],
      routesPending: [],
      mapEnabled:
        getEnv("VUE_APP_MAP_ENABLED") === "true" &&
        getEnv("VUE_APP_LOCATION_DISABLED") !== "true",
      canDrillDown: false,
      hasNeededItems: false,
      showDrillDownModal: false,
      showItemsNeededModal: false,
      hasWorkflow: false,
    };
  },
  methods: {
    ...mapActions(["getWelcome", "requestMyLocation"]),
    checkRoutes(routes) {
      let route = {};
      for (route of routes) {
        if (route.completedSites === route.totalSites) {
          this.routesCompleted.push(route);
        } else if (
          route.inProgressSites > 0 ||
          route.inRevision > 0 ||
          route.returned > 0
        ) {
          this.routesInProgress.push(route);
        } else {
          this.routesPending.push(route);
        }
      }
    },
    showDrillDownMenu() {
      this.$router.push({ name: "DrillDownScreen" });
    },
    showError(msg) {
      this.$refs.userErrorBox.isVisible = true;
      this.errorMessage =
        msg != null
          ? msg.code || msg.params
            ? this.$t(msg.code, msg.params)
            : msg
          : this.$t("error.default.connection.message");
    },
    catchAction(error) {
      const toastElement = this.$refs.statusErrorToast;
      toastElement.error({
        message:
          error.response != null
            ? this.$t(error.response.code, error.response.params)
            : this.$t("error.default.connection.message"),
        header: this.$t("error.default.connection.title"),
        duration: 5000,
      });
    },
    closeDrillDownModal() {
      this.showDrillDownModal = false;
    },
    closeItemsNeededModal() {
      this.showItemsNeededModal = false;
    },
  },
  computed: {
    sitesCount() {
      return this.routesPending.length + this.routesInProgress.length;
    },
  },
  mounted() {
    // If we have a message from the router param, we show it as an error message
    if (this.message) {
      this.showError(this.message);
    }
  },
  created() {
    if (
      getEnv("VUE_APP_SHOULD_SHOW_CHAT")?.toLowerCase() === "true" &&
      getEnv("VUE_APP_INTERCOM_APP_ID") !== null &&
      !this.$store.getters.isLoginExternal()
    ) {
      const user = this.$store.getters.GetUserData();
      Intercom({
        app_id: getEnv("VUE_APP_INTERCOM_APP_ID"),
        user_id: user.userId,
        name: user.firstname + " " + user.lastname,
        email: user.email,
        created_at: new Date().getTime(),
        app: "Smart Field",
        project: user.projectName,
        rol: user.roles[0].role,
        rol_id: user.roles[0].id,
      });

      if (window.Intercom) {
        window.Intercom("update", {
          hide_default_launcher: false,
        });
      }
    }

    this.getWelcome()
      .then((data) => {
        if (!data.success) {
          this.showError(data.message);
        } else {
          this.progressBar = data.progressbar;
          this.hasNeededItems = data.hasNeededItems;
          this.hasWorkflow = data.hasWorkflow;
          this.checkRoutes(data.routes);
          this.canDrillDown = data.canDrillDown;

          this.$store.commit("setResetSurveyButtonHiddenForRoles", {
            newValue: data.resetSurveyButtonHiddenForRoles,
          });
        }
      })
      .catch((error) => {
        this.catchAction(error);
      });
    if (this.mapEnabled) {
      this.requestMyLocation();
    }
  },
};
</script>

<style scoped>
#content {
  background-color: #fafafa;
  min-height: 100vh;
}

p {
  font-size: 16px;
  margin: 0px;
  color: #4f4f4f;
}

b.title {
  font-size: 18px;
}

.completed {
  margin-top: 32px;
}

.btn--link {
  text-decoration: underline;
  cursor: pointer;
  font-weight: bold;
}
</style>
